// MARK://dhk8/admin-shell/pages/modules/CustomersPage
// CRM — registro de clientes. Personal + direcciones múltiples + entidades
// fiscales (varias razones sociales por cliente) + lealtad (puntos/visitas y
// nivel derivado de la config del tenant).
// Fuente: dhk8-crm-contacts.
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Star, Search, Phone, Mail, Plus, Trash2, MapPin, Receipt, Settings2 } from 'lucide-react'
import {
  listCustomers, getLoyaltyConfig, createCustomer,
  deriveTier, sourceLabel,
  type Customer, type LoyaltyConfig, type ContactAddress, type FiscalEntity, type CustomerInput,
} from '../../lib/clients/customers.js'
import { REGIMES } from '../../lib/clients/fiscal.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'

// Subtítulo de sección discreto — marca NintaiGO: --font-mono, --text-tertiary,
// uppercase pequeño. Conserva las curvas/tokens; agrupa el form por densidad.
function FormSection({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      margin: '4px 0 2px', fontFamily: 'var(--font-mono)', fontSize: 11,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)',
    }}>{children}</div>
  )
}

const USOS = ['G01', 'G02', 'G03', 'I01', 'I04', 'P01', 'CP01', 'S01']
const ADDR_LABELS = [
  { value: 'home', label: 'Casa' },
  { value: 'work', label: 'Trabajo' },
  { value: 'other', label: 'Otra' },
]

function money(cents: string): string {
  const n = Number(cents) / 100
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}
function primaryPhone(c: Customer): string | null {
  const p = c.phones.find((x) => x.isPrimary) ?? c.phones[0]
  return p?.phone ?? null
}
function primaryEmail(c: Customer): string | null {
  const e = c.emails.find((x) => x.isPrimary) ?? c.emails[0]
  return e?.email ?? null
}

// ── editor state ─────────────────────────────────────────────────────────────
interface DraftAddress extends Omit<ContactAddress, 'id'> { _k: string }
interface DraftFiscal extends Omit<FiscalEntity, 'id'> { _k: string }
interface Draft {
  firstName: string
  lastName: string
  notes: string
  phones: Array<{ _k: string; phone: string }>
  emails: Array<{ _k: string; email: string }>
  addresses: DraftAddress[]
  fiscalEntities: DraftFiscal[]
}

let keySeq = 0
const nk = () => `k${++keySeq}`

function emptyDraft(): Draft {
  return { firstName: '', lastName: '', notes: '', phones: [], emails: [], addresses: [], fiscalEntities: [] }
}
function emptyAddress(): DraftAddress {
  return { _k: nk(), label: 'home', street: '', city: '', state: '', country: 'México', zip: '', isPrimary: false }
}
function emptyFiscal(): DraftFiscal {
  return { _k: nk(), rfc: '', legalName: '', regimeCode: '612', zipCode: '', usoCfdi: 'G03', invoiceEmail: '', isDefault: false }
}

function draftToInput(d: Draft): CustomerInput {
  const phones = d.phones.filter((p) => p.phone.trim()).map((p, i) => ({ phone: p.phone.trim(), label: 'mobile', isPrimary: i === 0 }))
  const emails = d.emails.filter((e) => e.email.trim()).map((e, i) => ({ email: e.email.trim(), label: 'work', isPrimary: i === 0 }))
  const addresses = d.addresses.map((a) => ({
    label: a.label,
    street: a.street?.trim() || null,
    city: a.city?.trim() || null,
    state: a.state?.trim() || null,
    country: a.country?.trim() || null,
    zip: a.zip?.trim() || null,
    isPrimary: a.isPrimary,
  }))
  const fiscalEntities = d.fiscalEntities.map((f) => ({
    rfc: f.rfc.trim().toUpperCase(),
    legalName: f.legalName.trim(),
    regimeCode: f.regimeCode,
    zipCode: f.zipCode.trim(),
    usoCfdi: f.usoCfdi,
    invoiceEmail: f.invoiceEmail?.trim() || null,
    isDefault: f.isDefault,
  }))
  return {
    firstName: d.firstName.trim(),
    lastName: d.lastName.trim() || undefined,
    notes: d.notes.trim() || undefined,
    phones, emails, addresses, fiscalEntities,
  }
}

export default function CustomersPage() {
  const navigate = useNavigate()
  const list = useAsync((s) => listCustomers(s))
  const [loyalty, setLoyalty] = useState<LoyaltyConfig | null>(null)
  const [query, setQuery] = useState('')
  // La lista solo crea ("Nuevo"). El EDITAR de un cliente existente migró a la
  // ficha de detalle (/customers/crm/:id → botón Editar). La fila navega a la ficha.
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Draft>(emptyDraft())
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    getLoyaltyConfig().then((c) => { if (alive) setLoyalty(c) }).catch(() => { /* lealtad opcional */ })
    return () => { alive = false }
  }, [])

  const customers = list.data ?? []
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const rows = q
      ? customers.filter(
          (c) =>
            c.fullName.toLowerCase().includes(q) ||
            (primaryPhone(c) ?? '').includes(q) ||
            (primaryEmail(c) ?? '').toLowerCase().includes(q),
        )
      : customers
    return [...rows].sort((a, b) => b.loyaltyPoints - a.loyaltyPoints)
  }, [customers, query])

  const totalPoints = customers.reduce((a, c) => a + c.loyaltyPoints, 0)
  const loyaltyOn = loyalty?.enabled ?? false

  function openNew() {
    setDraft(emptyDraft())
    setSaveErr(null)
    setCreating(true)
  }

  async function save() {
    const input = draftToInput(draft)
    if (!input.firstName) { setSaveErr('El nombre es obligatorio.'); return }
    for (const f of input.fiscalEntities) {
      if (f.rfc.length < 10 || f.rfc.length > 13) { setSaveErr(`RFC inválido en entidad fiscal "${f.legalName || '(sin nombre)'}".`); return }
      if (!f.legalName) { setSaveErr('Toda entidad fiscal necesita razón social.'); return }
      if (f.zipCode.length !== 5) { setSaveErr(`CP inválido en entidad fiscal "${f.legalName}" (5 dígitos).`); return }
    }
    setSaving(true)
    setSaveErr(null)
    try {
      await createCustomer(input)
      setCreating(false)
      list.reload()
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Clientes</h1>
          <p className="adm-page__sub">Registro de clientes — datos personales, direcciones y facturación</p>
        </div>
        <div className="adm-page__actions">
          {loyaltyOn && (
            <span className="adm-badge adm-badge--ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Star size={13} aria-hidden /> {totalPoints.toLocaleString('es-MX')} pts en circulación
            </span>
          )}
          <Link to="/customers/loyalty" className="adm-btn adm-btn--sm">
            <Settings2 size={15} aria-hidden /> Lealtad
          </Link>
          <button className="adm-btn adm-btn--primary" onClick={openNew}>
            <Plus size={16} aria-hidden /> Nuevo cliente
          </button>
        </div>
      </div>

      <div className="adm-field" style={{ maxWidth: 380 }}>
        <div style={{ position: 'relative' }}>
          <Search size={15} aria-hidden style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
          <input
            className="adm-input"
            style={{ paddingLeft: 32 }}
            placeholder="Buscar por nombre, teléfono o correo"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <div className="adm-card adm-card--pad0">
          <div className="adm-list">
            {filtered.length === 0 && (
              <div className="adm-empty">
                {query ? 'Sin resultados para esa búsqueda.' : 'Aún no hay clientes registrados.'}
              </div>
            )}
            {filtered.map((c) => {
              const phone = primaryPhone(c)
              const email = primaryEmail(c)
              const tier = deriveTier(c.loyaltyPoints, loyalty)
              return (
                <button key={c.id} type="button" className="adm-row" onClick={() => navigate(`/customers/crm/${c.id}`)}>
                  <div className="adm-row__main">
                    <div className="adm-row__title">{c.fullName || 'Sin nombre'}</div>
                    <div className="adm-row__meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {phone && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Phone size={12} aria-hidden /> {phone}
                        </span>
                      )}
                      {email && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Mail size={12} aria-hidden /> {email}
                        </span>
                      )}
                      <span>{sourceLabel(c.source)}</span>
                      {c.addresses.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={12} aria-hidden /> {c.addresses.length} {c.addresses.length === 1 ? 'dirección' : 'direcciones'}
                        </span>
                      )}
                      {c.fiscalEntities.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Receipt size={12} aria-hidden /> {c.fiscalEntities.length} fiscal{c.fiscalEntities.length === 1 ? '' : 'es'}
                        </span>
                      )}
                      {c.totalOrdersCount > 0 && <span>{c.totalOrdersCount} órdenes · {money(c.totalSpentCents)}</span>}
                    </div>
                  </div>
                  <div className="adm-row__tail" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    {loyaltyOn && (
                      <>
                        <span
                          className="adm-badge adm-badge--ok"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13 }}
                        >
                          <Star size={12} aria-hidden /> {c.loyaltyPoints} pts
                        </span>
                        {tier && (
                          <span
                            className="adm-badge"
                            style={tier.color ? { background: `${tier.color}22`, color: tier.color } : undefined}
                          >
                            {tier.name}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <Sheet
        open={creating}
        title="Nuevo cliente"
        onClose={() => setCreating(false)}
        footer={
          <>
            <button className="adm-btn" onClick={() => setCreating(false)} disabled={saving}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={saving || !draft.firstName.trim()}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {saveErr && <div className="adm-banner adm-banner--danger">{saveErr}</div>}

          {/* ── Datos básicos ── */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <FormSection>Datos básicos</FormSection>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label className="adm-field">
                <span className="adm-field__label">Nombre(s) *</span>
                <input className="adm-input" value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
              </label>
              <label className="adm-field">
                <span className="adm-field__label">Apellidos</span>
                <input className="adm-input" value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
              </label>
            </div>

            <FormSection>Contacto</FormSection>
            <RepeatGroup
              label="Teléfonos"
              addLabel="Agregar teléfono"
              items={draft.phones}
              onAdd={() => setDraft({ ...draft, phones: [...draft.phones, { _k: nk(), phone: '' }] })}
              onRemove={(k) => setDraft({ ...draft, phones: draft.phones.filter((p) => p._k !== k) })}
              renderItem={(p) => (
                <input
                  className="adm-input"
                  style={{ flex: 1 }}
                  placeholder="55 1234 5678"
                  value={p.phone}
                  onChange={(e) => setDraft({ ...draft, phones: draft.phones.map((x) => (x._k === p._k ? { ...x, phone: e.target.value } : x)) })}
                />
              )}
            />

            <RepeatGroup
              label="Correos"
              addLabel="Agregar correo"
              items={draft.emails}
              onAdd={() => setDraft({ ...draft, emails: [...draft.emails, { _k: nk(), email: '' }] })}
              onRemove={(k) => setDraft({ ...draft, emails: draft.emails.filter((e) => e._k !== k) })}
              renderItem={(em) => (
                <input
                  className="adm-input"
                  style={{ flex: 1 }}
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={em.email}
                  onChange={(e) => setDraft({ ...draft, emails: draft.emails.map((x) => (x._k === em._k ? { ...x, email: e.target.value } : x)) })}
                />
              )}
            />

            <FormSection>Notas</FormSection>
            <label className="adm-field">
              <span className="adm-field__label">Notas internas</span>
              <textarea className="adm-textarea" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </label>
          </section>

          {/* ── Direcciones ── */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormSection><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><MapPin size={13} aria-hidden /> Direcciones</span></FormSection>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => setDraft({ ...draft, addresses: [...draft.addresses, emptyAddress()] })}>
                <Plus size={14} aria-hidden /> Agregar dirección
              </button>
            </div>
            {draft.addresses.length === 0 && <p className="adm-page__sub" style={{ margin: 0 }}>Sin direcciones registradas.</p>}
            {draft.addresses.map((a) => (
              <div key={a._k} className="adm-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <label className="adm-field" style={{ flex: 1 }}>
                    <span className="adm-field__label">Tipo</span>
                    <select
                      className="adm-select"
                      value={a.label}
                      onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, label: e.target.value } : x)) })}
                    >
                      {ADDR_LABELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </label>
                  <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" aria-label="Quitar dirección" onClick={() => setDraft({ ...draft, addresses: draft.addresses.filter((x) => x._k !== a._k) })}>
                    <Trash2 size={15} aria-hidden />
                  </button>
                </div>
                <label className="adm-field">
                  <span className="adm-field__label">Calle y número</span>
                  <input className="adm-input" value={a.street ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, street: e.target.value } : x)) })} />
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label className="adm-field">
                    <span className="adm-field__label">Ciudad</span>
                    <input className="adm-input" value={a.city ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, city: e.target.value } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">Estado</span>
                    <input className="adm-input" value={a.state ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, state: e.target.value } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">C.P.</span>
                    <input className="adm-input" value={a.zip ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, zip: e.target.value } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">País</span>
                    <input className="adm-input" value={a.country ?? ''} onChange={(e) => setDraft({ ...draft, addresses: draft.addresses.map((x) => (x._k === a._k ? { ...x, country: e.target.value } : x)) })} />
                  </label>
                </div>
              </div>
            ))}
          </section>

          {/* ── Entidades fiscales ── */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormSection><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Receipt size={13} aria-hidden /> Información fiscal</span></FormSection>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => setDraft({ ...draft, fiscalEntities: [...draft.fiscalEntities, emptyFiscal()] })}>
                <Plus size={14} aria-hidden /> Agregar entidad
              </button>
            </div>
            <p className="adm-page__sub" style={{ margin: 0 }}>
              Razones sociales a las que se le emite factura. Opcional — un cliente puede registrar varias.
            </p>
            {draft.fiscalEntities.map((f) => (
              <div key={f._k} className="adm-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <label className="adm-field" style={{ flex: 1 }}>
                    <span className="adm-field__label">RFC *</span>
                    <input
                      className="adm-input"
                      value={f.rfc}
                      maxLength={13}
                      onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, rfc: e.target.value.toUpperCase() } : x)) })}
                    />
                  </label>
                  <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" aria-label="Quitar entidad fiscal" onClick={() => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.filter((x) => x._k !== f._k) })}>
                    <Trash2 size={15} aria-hidden />
                  </button>
                </div>
                <label className="adm-field">
                  <span className="adm-field__label">Razón social *</span>
                  <input className="adm-input" value={f.legalName} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, legalName: e.target.value } : x)) })} />
                </label>
                <label className="adm-field">
                  <span className="adm-field__label">Régimen fiscal</span>
                  <select className="adm-select" value={f.regimeCode} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, regimeCode: e.target.value } : x)) })}>
                    {REGIMES.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
                  </select>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label className="adm-field">
                    <span className="adm-field__label">C.P. fiscal *</span>
                    <input className="adm-input" value={f.zipCode} maxLength={5} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, zipCode: e.target.value.replace(/\D/g, '') } : x)) })} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field__label">Uso de CFDI</span>
                    <select className="adm-select" value={f.usoCfdi} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, usoCfdi: e.target.value } : x)) })}>
                      {USOS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field__label">Correo para facturas</span>
                  <input className="adm-input" type="email" value={f.invoiceEmail ?? ''} onChange={(e) => setDraft({ ...draft, fiscalEntities: draft.fiscalEntities.map((x) => (x._k === f._k ? { ...x, invoiceEmail: e.target.value } : x)) })} />
                </label>
              </div>
            ))}
          </section>

          {/* Métricas/origen/etiquetas son derivadas y solo-lectura → viven en la
              ficha de detalle (/customers/crm/:id), no en el alta. */}
        </div>
      </Sheet>
    </div>
  )
}

// Grupo repetible para listas simples (teléfonos, correos).
function RepeatGroup<T extends { _k: string }>(props: {
  label: string
  addLabel: string
  items: T[]
  onAdd: () => void
  onRemove: (k: string) => void
  renderItem: (item: T) => React.ReactNode
}) {
  return (
    <div className="adm-field">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="adm-field__label">{props.label}</span>
        <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={props.onAdd}>
          <Plus size={13} aria-hidden /> {props.addLabel}
        </button>
      </div>
      {props.items.map((it) => (
        <div key={it._k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {props.renderItem(it)}
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" aria-label="Quitar" onClick={() => props.onRemove(it._k)}>
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      ))}
    </div>
  )
}
